import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import api from '../config/api';

const UserPermissions = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Modül ve yetki tanımları
  const modules = [
    { id: 'dashboard', name: 'Dashboard', actions: ['view'] },
    { id: 'homepage_logistics', name: 'Anasayfa - Lojistik', actions: ['create'] },
    { id: 'homepage_production', name: 'Anasayfa - Üretim', actions: ['create'] },
    { id: 'homepage_stock_in', name: 'Anasayfa - Stok Giriş', actions: ['create'] },
    { id: 'homepage_stock_out', name: 'Anasayfa - Stok Çıkış', actions: ['create'] },
    { id: 'homepage_project', name: 'Anasayfa - Proje Kayıt', actions: ['create'] },
    { id: 'projects', name: 'Projeler', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'stock', name: 'Stok Kayıtları', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'production', name: 'Üretim Listesi', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'shipment', name: 'Sevkiyat Listesi', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'users', name: 'Kullanıcılar', actions: ['view', 'create', 'edit', 'delete'] }
  ];

  const actionLabels = {
    view: 'Görüntüleme',
    create: 'Ekleme',
    edit: 'Düzenleme',
    delete: 'Silme'
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setPermissions(user.permissions || {});
    setMessage({ type: '', text: '' });
  };

  const togglePermission = (moduleId, action) => {
    setPermissions(prev => {
      const updated = { ...prev };
      if (!updated[moduleId]) {
        updated[moduleId] = {};
      }
      updated[moduleId][action] = !updated[moduleId][action];
      return updated;
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      await api.put(`/users/${selectedUser.id}`, {
        ...selectedUser,
        permissions: permissions
      });
      setMessage({ type: 'success', text: '✅ Yetkiler başarıyla kaydedildi!' });
      loadUsers();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kaydetme başarısız!' });
    }
  };

  const hasPermission = (moduleId, action) => {
    return permissions[moduleId]?.[action] || false;
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: COLORS.textLight, fontSize: '1.1rem' }}>
          ⚠️ Bu sayfaya erişim yetkiniz bulunmamaktadır.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: COLORS.text, margin: 0 }}>
          👥 Kullanıcı Yetkilendirme
        </h2>
        <p style={{ color: COLORS.textLight, marginTop: '0.5rem' }}>
          Her kullanıcı için modül bazlı yetkiler tanımlayın
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div
          style={{
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
          }}
        >
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }}>
        {/* Kullanıcı Listesi */}
        <div style={{
          background: COLORS.white,
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          height: 'fit-content'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
            Kullanıcılar
          </h3>
          {loading ? (
            <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Yükleniyor...</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: selectedUser?.id === user.id ? COLORS.primary : 'transparent',
                    color: selectedUser?.id === user.id ? COLORS.white : COLORS.text,
                    border: `2px solid ${selectedUser?.id === user.id ? COLORS.primary : COLORS.background}`,
                    transition: 'all 0.2s',
                    fontWeight: selectedUser?.id === user.id ? '600' : '500'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedUser?.id !== user.id) {
                      e.currentTarget.style.background = COLORS.background;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedUser?.id !== user.id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ fontWeight: '600' }}>{user.full_name || user.username}</div>
                  <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', opacity: 0.8 }}>
                    {user.email}
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    marginTop: '0.5rem', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    background: selectedUser?.id === user.id ? 'rgba(255,255,255,0.2)' : COLORS.background,
                    display: 'inline-block'
                  }}>
                    {user.role === 'admin' ? 'Admin' : user.role === 'operator' ? 'Operatör' : 'Görüntüleyici'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Yetkilendirme Tablosu */}
        <div style={{
          background: COLORS.white,
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          {!selectedUser ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: COLORS.textLight, fontSize: '1.1rem' }}>
                ← Yetkilendirme yapmak için bir kullanıcı seçin
              </p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, margin: 0 }}>
                    {selectedUser.full_name || selectedUser.username}
                  </h3>
                  <p style={{ color: COLORS.textLight, fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    {selectedUser.email}
                  </p>
                </div>
                <button
                  onClick={handleSave}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: COLORS.primary,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  💾 Kaydet
                </button>
              </div>

              {/* Permissions Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: COLORS.background }}>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: COLORS.text,
                        borderRadius: '8px 0 0 0'
                      }}>
                        Modül
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>
                        Görüntüleme
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>
                        Ekleme
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: COLORS.text }}>
                        Düzenleme
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: COLORS.text,
                        borderRadius: '0 8px 0 0'
                      }}>
                        Silme
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((module, index) => (
                      <tr 
                        key={module.id}
                        style={{ 
                          borderBottom: `1px solid ${COLORS.background}`,
                          background: index % 2 === 0 ? '#fff' : '#fafafa'
                        }}
                      >
                        <td style={{ padding: '1rem', fontWeight: '500', color: COLORS.text }}>
                          {module.name}
                        </td>
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <td key={action} style={{ padding: '1rem', textAlign: 'center' }}>
                            {module.actions.includes(action) ? (
                              <input
                                type="checkbox"
                                checked={hasPermission(module.id, action)}
                                onChange={() => togglePermission(module.id, action)}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                  accentColor: COLORS.primary
                                }}
                              />
                            ) : (
                              <span style={{ color: COLORS.textLight }}>-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPermissions;
